'use client';

import { useState } from 'react';
import Booker from "@/components/Booker";
import { contactPageConfig } from "@/constant/contact";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/ui/components/tabs";
import { siteSettings } from "@workspace/utils";
import Link from "next/link";
import { isEmpty } from 'lodash';
import { AlertDescription } from '@workspace/ui/components/alert';

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    message: string;
}

interface FieldError {
    field: string;
    message: string;
}

interface ErrorResponse {
    message?: string;
    errors?: FieldError[];
}

function Disclaimer() {
    return (
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            By submitting this form, you agree to {' '}{siteSettings.siteName}&apos;s{' '}
            <Link href="/legal/terms" className="underline">
                Terms &amp; Conditions
            </Link>
            ,{' '}
            <Link href="/legal/privacy-policy" className="underline">
                Privacy Policy
            </Link>
            , and{' '}
            <Link href="/legal/refund-policy" className="underline">
                Refund &amp; Payment Policy
            </Link>
            . You also consent to being contacted via email, phone, or WhatsApp for service-related communication.
            We do not share your information with third parties without your consent, and you may withdraw your consent at any time
        </p>
    );
}

export function ContactTabForm() {
    const { form, schedule } = contactPageConfig;
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: ''
    });
    const [errorResponse, setErrorResponse] = useState<ErrorResponse | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/general-queries`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();
            console.log(data, response.ok);

            if (response.ok) {
                setSubmitStatus('success');
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phoneNumber: '',
                    message: ''
                });
            } else {
                setSubmitStatus('error');
                setErrorResponse(data);
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="section_demo">
            <div className="demo_sidebar dark:text-white text-black">
                <Tabs defaultValue="schedule">
                    <TabsList className="bg-transparent">
                        <TabsTrigger className="text-md" value="schedule">Schedule a Call</TabsTrigger>
                        <TabsTrigger className="text-md" value="general">General Queries</TabsTrigger>
                    </TabsList>

                    <TabsContent value="general">
                        <div className="contact_form-wrapper pt-3">
                            <div className="form_component w-form">
                                <form className="form" aria-label="Contact Form" onSubmit={handleSubmit}>
                                    <div className="form_row is-splitted">
                                        <div className="form_item">
                                            <label className="input_label">{form.fields[0]?.label}</label>
                                            <input
                                                className="input_field w-input"
                                                name="firstName"
                                                type="text"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="form_item">
                                            <label className="input_label">{form.fields[1]?.label}</label>
                                            <input
                                                className="input_field w-input"
                                                name="lastName"
                                                type="text"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form_row">
                                        <div className="form_item">
                                            <label className="input_label">{form.fields[2]?.label}</label>
                                            <input
                                                className="input_field w-input"
                                                name="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="form_row">
                                        <div className="form_item">
                                            <label className="input_label">
                                                {form.fields[3]?.label}
                                                {form.fields[3]?.optional &&
                                                    <span className="text-size-tiny text-weight-normal margin-left margin-tiny text-[var(--gray--80)] dark:text-[var(--gray--20)]">
                                                        (optional)
                                                    </span>
                                                }
                                            </label>
                                            <input
                                                className="input_field w-input"
                                                name="phoneNumber"
                                                type="tel"
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="form_row">
                                        <div className="form_item">
                                            <label className="input_label">{form.fields[4]?.label}</label>
                                            <textarea
                                                className="input_field is-text-area w-input"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                required
                                            ></textarea>
                                        </div>
                                    </div>

                                    <input
                                        type="submit"
                                        className="button is-fluid w-button"
                                        value={isSubmitting ? 'Sending...' : form.submitText}
                                        disabled={isSubmitting}
                                    />

                                    {(submitStatus === 'error' && !isEmpty(errorResponse)) && (
                                        <div className="form_validation is-error" role="region" aria-label="Contact Form failure">
                                            <div className="text-size-small">{errorResponse?.message}</div>
                                            <AlertDescription>
                                                {errorResponse.errors && errorResponse.errors.length > 0 && (
                                                    <div className="mt-2">
                                                        {errorResponse.errors.map((error: FieldError, index: number) => (
                                                            <div key={index} className="text-sm">
                                                                <span className="font-medium">{error.field}:</span> {error.message}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </AlertDescription>
                                        </div>
                                    )}
                                    {submitStatus === 'success' && (
                                        <div className="form_validation " role="region" aria-label="Contact Form success">
                                            <div className="margin-top margin-medium">
                                                <div className="margin-bottom margin-small">
                                                    <div className="margin-bottom margin-xsmall">
                                                        <div className="heading-xxsmall">{form.success.title}</div>
                                                    </div>
                                                    <div>{form.success.message}</div>
                                                </div>
                                                <Link href={form.success.link.href} className="link w-inline-block">
                                                    <div className="link_content-wrapper">
                                                        <div className="link_text">{form.success.link.text}</div>
                                                    </div>
                                                </Link>
                                            </div>
                                        </div>
                                    )}

                                    <div className="margin-top margin-xsmall">
                                        <Disclaimer />
                                    </div>
                                </form>

                                

                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="schedule">
                        <div className="contact_form-wrapper padding-tiny bg-[var(--primary--beige)] dark:bg-[var(--gray--100)]">
                            <Booker
                                title="book a call with us"
                                calendarUrl={schedule.calendarUrl}
                            />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
            <div className="demo_background bg-[var(--pink--50)] dark:bg-[var(--pink--800)]"></div>
        </div>
    );
}